import React from 'react';
import { Brain, Loader2, RefreshCw, Upload } from 'lucide-react';

interface Props {
  processingCount: number;
  loading: boolean;
  onRefresh: () => void;
  onUpload: () => void;
}

/**
 * Brand-gradient hero for the AI training corpus page. Mobile-first,
 * stacked rows; collapses cleanly down to phone widths via Tailwind
 * responsive utilities.
 */
export const CorpusHero: React.FC<Props> = ({
  processingCount,
  loading,
  onRefresh,
  onUpload,
}) => {
  return (
    <header
      className="
        relative overflow-hidden mb-3 text-white
        rounded-2xl sm:rounded-3xl
        px-4 sm:px-6 py-5 sm:py-6
        bg-gradient-to-br from-[#1a5276] to-[#0e9172]
        shadow-[0_14px_32px_rgba(15,82,118,0.26)]
      "
      style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
    >
      {/* Decorative orbs */}
      <span className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10 blur-md sm:-right-20 sm:-top-24 sm:h-72 sm:w-72" />
      <span className="pointer-events-none absolute -left-14 -bottom-24 h-44 w-44 rounded-full bg-[#5ecdb0]/30 blur-md sm:-left-20 sm:h-56 sm:w-56" />

      {/* Row 1: chips + refresh */}
      <div className="relative flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/20 backdrop-blur px-3 py-1 text-[10.5px] font-extrabold uppercase tracking-[0.12em]">
          <Brain size={11} /> AI training corpus
        </span>
        {processingCount > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#5ecdb0]/40 bg-[#5ecdb0]/20 px-3 py-1 text-[10.5px] font-extrabold uppercase tracking-wide text-[#d4f5ec]">
            <Loader2 size={11} className="animate-spin" />
            {processingCount} processing
          </span>
        )}
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh"
          title="Refresh"
          className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/20 bg-white/20 backdrop-blur hover:bg-white/30 disabled:opacity-60 disabled:cursor-not-allowed transition"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Row 2: headline */}
      <h1
        className="relative mt-3 mb-1.5 leading-tight tracking-tight"
        style={{
          fontFamily: "'Instrument Serif', serif",
          fontSize: 'clamp(22px, 6vw, 34px)',
        }}
      >
        Teach the model with your <em className="not-italic font-normal italic text-[#5ecdb0]">past papers</em>.
      </h1>

      {/* Row 3: subtitle — clamped to 3 lines on mobile */}
      <p className="relative max-w-xl text-[12.5px] sm:text-[13.5px] leading-relaxed opacity-90 line-clamp-3 sm:line-clamp-none">
        Drop CDACC past papers, internal exams or sample question banks here.
        We parse, chunk and embed each one into the institution's self-hosted
        Llama corpus so future generations sound like you.
      </p>

      {/* Row 4: CTA — full width on mobile, inline on tablet+ */}
      <div className="relative mt-4 sm:inline-flex">
        <button
          type="button"
          onClick={onUpload}
          className="
            w-full sm:w-auto inline-flex items-center justify-center gap-1.5
            rounded-xl bg-white px-4 py-2.5
            text-[12.5px] font-bold text-[#1a5276]
            shadow-[0_6px_18px_rgba(0,0,0,0.16)]
            hover:-translate-y-0.5 transition
          "
        >
          <Upload size={14} /> Upload past papers
        </button>
      </div>
    </header>
  );
};
